import {
  createSlice,
  createEntityAdapter,
  EntityState,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import axios from 'axios';
import { createNewTask } from 'api/newTask';
import { fetchTaskStatusList, TaskStatus } from 'api/taskStatusList';
import {
  fetchTaskTypeList,
  fetchTaskTypeListV3,
  TaskType,
} from 'api/taskTypeList';
import { TaskAssignee, TaskProject } from 'types/store/tasks';
import { NewTask } from 'types/store/newTask';
import { fetchTeamTasks } from '../teamTasks/teamTasksSlice';
import { RootState } from 'state/store';

const assigneesAdapter = createEntityAdapter<TaskAssignee>();
const projectsAdapter = createEntityAdapter<TaskProject>();
const statusesAdapter = createEntityAdapter<TaskStatus>();
const typesAdapter = createEntityAdapter<TaskType>();

interface AllTasksState {
  assignees: EntityState<TaskAssignee>;
  projects: EntityState<TaskProject>;
  statuses: EntityState<TaskStatus>;
  types: EntityState<TaskType>;
  taskTypesLoading?: boolean;
  taskStatusesLoading?: boolean;
  shouldRefreshTasks?: boolean;
}

const initialState: AllTasksState = {
  assignees: assigneesAdapter.getInitialState(),
  projects: projectsAdapter.getInitialState(),
  statuses: statusesAdapter.getInitialState(),
  types: typesAdapter.getInitialState(),
};

interface CreateNewTaskParams {
  newTasks: NewTask[];
  userId: string;
  sendNotifications?: boolean;
}

export const fetchTaskTypes = createAsyncThunk(
  'normalized/tasksList/allTasks/fetchTaskTypes',
  async (useV3: boolean | undefined, { rejectWithValue }) => {
    try {
      const response = useV3
        ? await fetchTaskTypeListV3()
        : await fetchTaskTypeList();
      return response.taskTypes;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue('Error fetching team tasks');
    }
  }
);

export const fetchTaskStatuses = createAsyncThunk(
  'normalized/tasksList/allTasks/fetchTaskStatues',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchTaskStatusList();
      return response.taskStatuses;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue('Error fetching team tasks');
    }
  }
);

export const createTask = createAsyncThunk(
  'normalized/tasksList/allTasks/createTask',
  async (
    { newTasks, userId, sendNotifications = true }: CreateNewTaskParams,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await createNewTask({
        newTasks,
        sendNotifications,
      });
      dispatch(fetchTeamTasks({}));
      // dispatch(fetchMyTasks({ userId }));
      return response.successes[0];
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(error);
    }
  }
);

const allTasksSlice = createSlice({
  name: 'normalized/tasksList/allTasks',
  initialState,
  reducers: {
    setTasksAssignees: (state, action) => {
      assigneesAdapter.upsertMany(state.assignees, action.payload);
    },
    setTasksProjects: (state, action) => {
      projectsAdapter.upsertMany(state.projects, action.payload);
    },
    setShouldRefreshTasks: (state, action) => {
      state.shouldRefreshTasks = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskTypes.pending, (state) => {
        state.taskTypesLoading = true;
      })
      .addCase(fetchTaskTypes.fulfilled, (state, action) => {
        state.taskTypesLoading = false;

        typesAdapter.upsertMany(state.types, action.payload);
      })
      .addCase(fetchTaskTypes.rejected, (state) => {
        state.taskTypesLoading = false;
      })
      .addCase(fetchTaskStatuses.pending, (state) => {
        state.taskStatusesLoading = true;
      })
      .addCase(fetchTaskStatuses.fulfilled, (state, action) => {
        state.taskStatusesLoading = false;

        statusesAdapter.upsertMany(state.statuses, action.payload);
      })
      .addCase(fetchTaskStatuses.rejected, (state) => {
        state.taskStatusesLoading = false;
      })
      .addCase(createTask.rejected, (state, action) => {
        throw new Error(action.payload as string);
      });
  },
});

export const { setTasksAssignees, setTasksProjects, setShouldRefreshTasks } =
  allTasksSlice.actions;

export const { selectEntities: selectTaskAssignees } =
  assigneesAdapter.getSelectors(
    (state: RootState) => state.normalized.tasksList.allTasks.assignees
  );

export const { selectEntities: selectTaskProjects } =
  projectsAdapter.getSelectors(
    (state: RootState) => state.normalized.tasksList.allTasks.projects
  );

export const { selectEntities: selectTaskStatuses } =
  statusesAdapter.getSelectors(
    (state: RootState) => state.normalized.tasksList.allTasks.statuses
  );

export const { selectEntities: selectTaskTypes } = typesAdapter.getSelectors(
  (state: RootState) => state.normalized.tasksList.allTasks.types
);

export const selectShouldRefreshTasks = (state: RootState) =>
  state.normalized.tasksList.allTasks.shouldRefreshTasks;

export const selectTaskTypesLoadingStatus = (state: RootState) =>
  state.normalized.tasksList.allTasks.taskTypesLoading;

export const selectTaskStatusesLoadingStatus = (state: RootState) =>
  state.normalized.tasksList.allTasks.taskStatusesLoading;

export const selectCanFetchTaskTypes = createSelector(
  [selectTaskTypes, selectTaskTypesLoadingStatus],
  (taskTypes, areTaskTypesLoading) => {
    return Object.keys(taskTypes).length === 0 && areTaskTypesLoading === false;
  }
);

export const selectCanFetchTaskStatues = createSelector(
  [selectTaskStatuses, selectTaskStatusesLoadingStatus],
  (taskStatues, areTaskStatusesLoading) => {
    return (
      Object.keys(taskStatues).length === 0 && areTaskStatusesLoading === false
    );
  }
);

export const selectTaskTypeByCode = createSelector(
  [selectTaskTypes, (_: RootState, taskTypeCode: string) => taskTypeCode],
  (taskTypes, taskTypeCode) => taskTypes[taskTypeCode] ?? undefined
);

export const selectAssigneesListByIds = createSelector(
  selectTaskAssignees,
  (_: RootState, assigneesIds: string[]) => assigneesIds,
  (taskAssignees, assigneesIds) => {
    const assignees: TaskAssignee[] = [];
    assigneesIds.forEach((id) => {
      if (id in taskAssignees) {
        assignees.push(taskAssignees[id] as TaskAssignee);
      }
    });
    return assignees;
  }
);

export const selectTaskProjectById = createSelector(
  [selectTaskProjects, (_: RootState, projectId: string) => projectId],
  (taskProjects, projectId) => taskProjects[projectId] ?? ({} as TaskProject)
);

export default allTasksSlice.reducer;
